<template>
	<div
			class="device-selector-section"
			:class="{
				outlined: store.pairedTVs.length && (!canSendCommands || modelValue.length === 0),
				'has-scroll': tvs.length > 4
			}"
	>
		<div v-if="!store.pairedTVs.length" class="empty-state">
			<p class="empty-text">
				No displays paired yet. Displays will appear here when paired.
				<Divider/>
				Press <span class="pair-icon"><Icon name="plus" size="xs"/></span> to pair a new display
			</p>
		</div>
		
		<div v-if="store.pairedTVs.length" class="device-list">
			<!-- All Devices (non-clickable) -->
			<div class="device-option device-option-header">
				<label class="device-label device-label-header">
					<Icon name="tv-groups" size="sm"/>
					<span>All Displays</span>
					<span class="device-count"> {{ onlineCount }}/{{ tvs.length }} online</span>
				</label>
			</div>
			
			<!-- Individual TVs -->
			<div
					v-for="tv in sortedTVs"
					:key="tv.id"
					class="device-option"
					:class="{ selected: modelValue[0] === tv.id }"
			>
				<input
						:id="`device-${tv.id}`"
						:checked="modelValue[0] === tv.id"
						type="radio"
						:name="'device-selection'"
						:value="tv.id"
						:disabled="tv.status === 'offline'"
						@change="handleDeviceChange(tv.id)"
				>
				<label :for="`device-${tv.id}`" class="device-label">
					<Icon name="tv" size="sm"/>
<!--					<span class="device-name">{{ tv.name }}</span>-->
					<span class="device-name">
						<span class="base-text">{{ splitDeviceName(tv.name).base }}</span>
						<span v-if="splitDeviceName(tv.name).suffix" class="suffix-text">{{ splitDeviceName(tv.name).suffix }}</span>
					</span>
					<div class="device-status" :class="[
					  tv.status === 'offline' ? 'offline' : 'online',
					  { loading: ['loading', 'rebooting', 'busy'].includes(tv.status) }
					]">
						<div class="status-dot"/>
						<span>{{ tv.status }}</span>
<!--						TODO: re-implement user-settable device name on new BE and ensure device info returned
								here is accurate then uncomment -->
<!--						<Icon name="id" size="sm" @click.stop.prevent="openDeviceInfo(tv)"/>-->
					</div>
				</label>
			</div>
		</div>
		
		<Hint
				v-if="store.pairedTVs.length && (!canSendCommands || modelValue.length === 0)"
				position="absolute"
				arrow-direction="up"
				center-x
		>
			<span>Select a Display</span>
		</Hint>
		
		<Hint
				v-if="!store.pairedTVs.length"
				position="fixed"
				arrow-direction="up"
				target-selector="#pair-btn"
				:offsets="{ top: 'var(--spacing-4xl)', right: 'var(--spacing-3xl)' }"
		>
			<span>Pair a Display</span>
		</Hint>
		
		<Modal
				:show="!!selectedDevice"
				title="Device Info"
				:show-confirm="false"
				cancel-text="Close"
				:confirm-disabled="!hasChanges"
				@confirm="saveDevice"
				@cancel="selectedDevice = null"
				@close="selectedDevice = null"
		>
			<div v-if="selectedDevice" class="form-group">
				<div class="form-group">
					<label>Name</label>
					<input v-model="formData.name" type="text" maxlength="30">
				</div>
				<div class="form-group">
					<label>Default App</label>
					<input :value="selectedDevice.default_app?.label ?? 'None'" type="text" disabled>
				</div>
				<div class="form-row">
					<div class="form-group">
						<label>Serial No.</label>
						<input :value="selectedDevice.identifier_code" type="text" disabled>
					</div>
					<div class="form-group">
						<label>Status</label>
						<input :value="selectedDevice.status" type="text" disabled>
					</div>
				</div>
<!--				<div class="form-group">-->
<!--					<label>Group</label>-->
<!--					<input :value="selectedDevice.group?.name ?? 'None'" type="text" disabled>-->
<!--				</div>-->
				<div class="form-row">
					<div class="form-group">
						<label>Last Seen</label>
						<input :value="selectedDevice.last_seen_at ? new Date(selectedDevice.last_seen_at).toLocaleString() : 'Never'"
							   type="text" disabled>
					</div>
					<div class="form-group">
						<label>Added</label>
						<input :value="selectedDevice.created_at ? new Date(selectedDevice.created_at).toLocaleDateString() : '—'"
							   type="text" disabled>
					</div>
				</div>
				<p v-if="formError" class="form-error">{{ formError }}</p>
			</div>
		</Modal>
	</div>
</template>

<script setup>
import {computed, ref, watch} from 'vue'
import Icon from './Icon.vue'
import {useMainStore} from "../store/main.js";
import Modal from "./Modal.vue";
import {updateDevice} from "../utils/api.js";
import Hint from "@/components/Hint.vue";
import Divider from "@/components/Divider.vue";

const store = useMainStore()
const props = defineProps({
	modelValue: {type: Array, required: true}, // selectedDeviceIds
	tvs: {type: Array, required: true}, // store.pairedTVs
	canSendCommands: {type: Boolean, default: false},
})

const emit = defineEmits(['update:modelValue'])

const selectedDevice = ref(null)

function openDeviceInfo(tv) {
	selectedDevice.value = tv
}

const formData = ref({})
const formError = ref(null)

watch(selectedDevice, (tv) => {
	formData.value = tv ? {name: tv.name} : {}
	formError.value = null
})

const hasChanges = computed(() => {
	if (!selectedDevice.value) return false
	return Object.keys(formData.value).some(
			key => formData.value[key] !== selectedDevice.value[key]
	)
})

async function saveDevice() {
	if (!selectedDevice.value || !hasChanges.value) return
	const result = await updateDevice(selectedDevice.value.id, formData.value)
	if (result.success) {
		await store.refreshSiteData()
		closeDeviceModal()
	} else {
		formError.value = result.error
	}
}

function closeDeviceModal() {
	selectedDevice.value = null
	formData.value = {}
	formError.value = null
}

function splitDeviceName(name) {
	const match = name.match(/^(.*?)(\s\d+)$/)
	if (match) {
		return {base: match[1], suffix: match[2]}
	}
	return {base: name, suffix: ''}
}

const sortedTVs = computed(() =>
	[...props.tvs].sort((a, b) => (a.status === 'offline') - (b.status === 'offline'))
)

const onlineCount = computed(() =>
	props.tvs.filter(t =>
		!['offline', 'loading'].includes(t.status)
	).length
)

function handleDeviceChange(changedId) {
	// Single selection: emit array with only the selected device ID
	emit('update:modelValue', [changedId])
}
</script>

<style scoped>
.empty-state {
	padding: var(--spacing-lg) var(--spacing-md);
}

.device-selector-section {
	position: relative;
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-lg);
	background: var(--color-surface-2);
	margin-bottom: var(--spacing-lg);
}

.device-selector-section.has-scroll::before {
	content: '';
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	height: 2rem;
	background: linear-gradient(to top, var(--color-surface) 0%, transparent 100%);
	pointer-events: none;
	z-index: 1;
	border-bottom-left-radius: calc(var(--border-radius-lg) - 0.3rem );
	border-bottom-right-radius: calc(var(--border-radius-lg) - 0.3rem);
}

.device-selector-section.outlined::after {
	content: '';
	position: absolute;
	inset: 0;
	border-radius: var(--border-radius-lg);
	box-shadow: inset 0 0 var(--spacing-sm) var(--spacing-xs) var(--color-error);
	pointer-events: none;
	z-index: 2;
	animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
	0%, 100% {
		box-shadow: inset 0 0 var(--spacing-xs) calc(var(--spacing-xs) * 0.6) var(--color-error);
	}
	50% {
		box-shadow: inset 0 0 calc(var(--spacing-xs)) calc(var(--spacing-xs)) var(--color-error);
	}
}

.device-list {
	max-height: 300px;
	overflow-y: auto;
}

.device-option {
	align-items: center;
	padding: 0;
	border-bottom: var(--border-thin) var(--color-border);
	text-shadow: var(--shadow-sm);
	position: relative;
}

.device-option:last-child {
	border-bottom: none;
	border-bottom-right-radius: var(--border-radius-md);
	border-bottom-left-radius: var(--border-radius-md);
}

/*.device-option:nth-child(2) {*/
/*	border-top-left-radius: var(--border-radius-md);*/
/*}*/

.device-option input[type="radio"] {
	display: none;
}

/* Non-clickable header row for All Devices */
.device-option-header .device-label-header {
	cursor: default;
}

.device-option.selected {
	background: var(--color-primary-selected);
	border-top-left-radius: var(--border-radius-md);
	border-bottom-left-radius: var(--border-radius-md);
	border-left: var(--border-thick) var(--color-primary);
}

.device-option.selected::before {
	content: '';
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: var(--border-thick);
	background: var(--color-primary);
	border-top-left-radius: var(--border-radius-md);
	border-bottom-left-radius: var(--border-radius-md);
}

.device-label {
	position: relative;
	flex: 1;
	display: flex;
	align-items: center;
	gap: var(--spacing-md);
	padding: var(--spacing-lg);
	cursor: pointer;
	transition: background var(--transition-fast);
}

.device-name {
	flex: 0 1 auto;
	min-width: 0;
	max-width: 65%;
	display: inline-flex;
	text-transform: capitalize;
}

.base-text {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
}

.suffix-text {
	flex-shrink: 0;
	white-space: pre;
}

.device-count {
	margin-left: auto;
	font-size: var(--font-size-xs);
	color: var(--color-text-secondary);
}

.device-status {
	position: absolute;
	right: var(--spacing-lg);
	text-transform: capitalize;
	margin-left: auto;
	display: flex;
	align-items: center;
	gap: var(--spacing-sm);
	font-size: var(--font-size-xs);
}

.device-status.online {
	color: var(--color-success);
}

.device-status.offline {
	color: var(--color-error);
}

.device-status.loading {
	color: var(--color-warning);
}

.status-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: currentColor;
}

.pair-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	vertical-align: middle;
	margin: 0 var(--spacing-xs);
	border: var(--border-thin) var(--color-primary);
	border-radius: var(--border-radius-md);
	padding: var(--spacing-sm);
}

/* Edit Device Form */
.form-group {
	display: flex;
	flex-direction: column;
	padding-top: var(--spacing-sm);
}

.form-row {
	display: flex;
}

.form-row .form-group {
	flex: 1;
}

.form-group label {
	font-weight: var(--font-weight-bold);
	color: var(--color-text);
	text-shadow: var(--shadow-md);
	text-align: start;
	padding-left: var(--spacing-sm);
}

.form-group input {
	padding: var(--spacing-md);
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-md);
	background: var(--color-surface);
	color: var(--color-text);
	text-transform: capitalize;
}

.form-group input:disabled {
	opacity: 0.6;
	cursor: not-allowed;
	background: transparent;
	border: none;
	padding: var(--spacing-xs);
	padding-left: var(--spacing-sm);
}

.form-error {
	margin-top: var(--spacing-md);
	color: var(--color-error);
}

@media (max-width: 480px) {
	.form-row {
		flex-direction: column;
	}
}
</style>